import React, { useState, useMemo, useCallback } from "react";
import { Card, Button, Input, Table, Space, Tag, Badge, Modal, message, Dropdown, Typography } from "antd";
import { useMutation } from "react-query";
import { useTableSelection, useLoadList, useQuickSearch } from "../../../hooks";
import { agentVariantEnhancedService } from "../../../api/enhancedApi";
import { formatDateTime } from "../../../utils/dateUtils";
import AgentVariantRuleFilter from "./components/AgentVariantRuleFilter";
import AgentVariantRuleForm from "./components/AgentVariantRuleForm";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, DownOutlined } from "@ant-design/icons";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import CollapsibleFilterBar from "../../../components/common/CollapsibleFilterBar";

const { confirm } = Modal;
const { Text, Paragraph } = Typography;

const AgentVariantRuleList = () => {
    // 获取权限相关功能
    const { hasPermission } = useAuth();

    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [formVisible, setFormVisible] = useState(false);
    const [currentRule, setCurrentRule] = useState(null);
    const [formMode, setFormMode] = useState("add");
    const [filterCollapsed, setFilterCollapsed] = useState(false);


    // 构造API请求参数
    const apiParams = React.useMemo(() => {
        const params = {
            page: pagination.current,
            page_size: pagination.pageSize,
            ...filterParams,
        };
        return params;
    }, [pagination, filterParams]);

    // 使用自定义Hook加载数据
    const { data: rulesData, isLoading, refetch } = useLoadList("Agent变体路由规则", agentVariantEnhancedService.getList, apiParams);

    // 删除规则
    const deleteMutation = useMutation({
        mutationFn: agentVariantEnhancedService.delete,
        onSuccess: (response) => {
            if (response.data.success) {
                message.success(response.data.message || "删除成功");
                refetch();
            } else {
                message.error(response.data.message || "删除失败");
            }
        },
        onError: (error) => {
            message.error(error.response?.data?.detail || "删除失败");
        },
    });

    // 使用自定义Hook处理表格选择
    const { selectedRowKeys, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection } = useTableSelection({
        getRowKey: (record) => record.id,
        dataSource: rulesData?.items || [],
        pagination,
    });

    // 渲染匹配条件
    const renderMatchConditions = (conditions) => {
        if (!conditions || Object.keys(conditions).length === 0) {
            return <Tag color="gold">默认规则</Tag>;
        }

        return (
            <Space wrap>
                {Object.entries(conditions).map(([key, value]) => (
                    <Tag key={key} color="blue">
                        {key}: {value}
                    </Tag>
                ))}
            </Space>
        );
    };

    // 定义表格列
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "本地Agent",
            dataIndex: "local_agent_name",
            key: "local_agent_name",
            width: 180,
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: "目标Agent",
            dataIndex: "target_agent_code",
            key: "target_agent_code",
            width: 200,
            ellipsis: true,
            render: (text) => <Text code>{text}</Text>,
        },
        {
            title: "匹配条件",
            dataIndex: "match_conditions",
            key: "match_conditions",
            width: 250,
            render: renderMatchConditions,
        },
        {
            title: "优先级",
            dataIndex: "priority",
            key: "priority",
            width: 80,
            align: "center",
            render: (priority) => <Tag color={priority > 0 ? "blue" : "default"}>{priority}</Tag>,
        },
        {
            title: "描述",
            dataIndex: "description",
            key: "description",
            width: 200,
            ellipsis: true,
            render: (text) => (
                <Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0 }}>
                    {text || "-"}
                </Paragraph>
            ),
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 80,
            render: (is_active) => <Badge className="status-badge" status={is_active ? "success" : "error"} text={is_active ? "启用" : "停用"} />,
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 160,
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 160,
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <PermissionGuard permission="agent:update">
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="agent:delete">
                        <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
                            删除
                        </Button>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    // 处理表格分页变化
    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
    };

    // 处理搜索
    const handleSearch = (values) => {
        const params = {};

        if (values.local_agent_name) {
            params.local_agent_name = values.local_agent_name;
        }
        if (values.is_active !== undefined && values.is_active !== null) {
            params.is_active = values.is_active;
        }
        if (values.description) {
            params.description = values.description;
        }

        setFilterParams(params);
        setPagination((prev) => ({
            ...prev,
            current: 1,
        }));
    };

    // 重置搜索
    const handleReset = () => {
        setFilterParams({});
        setPagination((prev) => ({
            ...prev,
            current: 1,
        }));
    };

    // 切换筛选器折叠状态
    const handleToggleFilterCollapse = () => {
        setFilterCollapsed((prev) => !prev);
    };

    // 快捷搜索
    const quickSearch = useQuickSearch({ setFilterParams, setPagination });

    // 筛选摘要项
    const filterSummaryItems = useMemo(() => {
        const items = [];
        if (filterParams.local_agent_name) items.push({ label: "Agent", value: filterParams.local_agent_name });
        if (filterParams.is_active !== undefined) {
            items.push({ label: "状态", value: filterParams.is_active ? "启用" : "停用" });
        }
        if (filterParams.description) items.push({ label: "描述", value: filterParams.description });
        return items;
    }, [filterParams]);

    // 添加规则
    const handleAdd = () => {
        setFormMode("add");
        setCurrentRule(null);
        setFormVisible(true);
    };

    // 编辑规则
    const handleEdit = (record) => {
        if (!hasPermission("agent:update")) {
            message.error("您没有编辑Agent变体路由规则的权限");
            return;
        }
        setFormMode("edit");
        setCurrentRule(record);
        setFormVisible(true);
    };

    // 删除规则
    const handleDelete = (record) => {
        if (!hasPermission("agent:delete")) {
            message.error("您没有删除Agent变体路由规则的权限");
            return;
        }

        if (!record || !record.id) {
            message.error("无效的记录数据");
            return;
        }

        confirm({
            title: "确认删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除 "${record.local_agent_name}" -> "${record.target_agent_code}" 的路由规则吗？此操作不可恢复。`,
            okText: "确认",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                deleteMutation.mutate(record.id);
            },
        });
    };

    // 批量删除
    const handleBatchDelete = () => {
        if (!hasPermission("agent:delete")) {
            message.error("您没有删除Agent变体路由规则的权限");
            return;
        }

        if (!selectedRowKeys || selectedRowKeys.length === 0) {
            message.warning("请先选择要删除的项目");
            return;
        }

        confirm({
            title: "确认批量删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除选中的 ${selectedRowKeys.length} 个路由规则吗？此操作不可恢复。`,
            okText: "确认",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                Promise.all(selectedRowKeys.map((id) => agentVariantEnhancedService.delete(id)))
                    .then((responses) => {
                        const successCount = responses.filter((res) => res.data.success).length;
                        const failureCount = responses.length - successCount;

                        if (successCount > 0) {
                            message.success(`成功删除 ${successCount} 个路由规则`);
                            clearSelection();
                            refetch();
                        }

                        if (failureCount > 0) {
                            console.error(`有 ${failureCount} 个删除操作失败`);
                        }
                    })
                    .catch((error) => {
                        message.error(`批量删除失败: ${error.message}`);
                    });
            },
        });
    };

    // 批量操作菜单项
    const batchActionItems = [
        {
            key: "delete",
            label: "批量删除",
            disabled: selectedRowKeys.length === 0 || !hasPermission("agent:delete"),
            onClick: handleBatchDelete,
        },
    ];

    // 表单提交成功
    const handleFormSuccess = () => {
        setFormVisible(false);
        refetch();
    };

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">Agent变体路由规则管理</div>
                <div className="page-action-compact">
                    <PermissionGuard permission="agent:create">
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                            添加规则
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="agent:delete">
                        <Dropdown menu={{ items: batchActionItems }} disabled={selectedRowKeys.length === 0}>
                            <Button>
                                批量操作 <DownOutlined />
                            </Button>
                        </Dropdown>
                    </PermissionGuard>
                </div>
            </div>

            {/* 搜索过滤 */}
            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleReset}
                drawerTitle="Agent变体路由规则 - 筛选条件"
                quickControls={
                    <Input
                        placeholder="搜索 Agent名称/描述..."
                        style={{ width: 240, height: 26 }}
                        {...quickSearch.inputProps}
                    />
                }
            >
                <AgentVariantRuleFilter onSearch={handleSearch} onReset={handleReset} collapsed={filterCollapsed} onToggleCollapse={handleToggleFilterCollapse} autoApply />
            </CollapsibleFilterBar>

            {/* 选中项统计 */}
            {selectedRowKeys.length > 0 && (
                <div className="batch-operations-info" style={{ marginBottom: 16 }}>
                    已选择{" "}
                    <span className="selected-count" style={{ fontWeight: "bold", color: "#1890ff" }}>
                        {selectedRowKeys.length}
                    </span>{" "}
                    项
                    <Button type="link" onClick={clearSelection} size="small">
                        清空
                    </Button>
                </div>
            )}

            {/* 规则列表 */}
            <Card className="data-card">
                <Table
                    ref={tableRef}
                    size="small"
                    rowSelection={hasPermission("agent:delete") ? rowSelection : null}
                    columns={columns}
                    dataSource={rulesData?.items || []}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        total: rulesData?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                        pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1400 }}
                    onRow={getRowProps}
                />
            </Card>

            {/* 添加/编辑表单 */}
            {formVisible && <AgentVariantRuleForm visible={formVisible} mode={formMode} initialValues={currentRule} onCancel={() => setFormVisible(false)} onSuccess={handleFormSuccess} />}
        </div>
    );
};

export default AgentVariantRuleList;
